import { useNavigate, useSearch } from "@tanstack/react-router";

import { useCreateRecipe, useRecipe } from "@/lib/api/recipes";
import { newULID } from "@/lib/api/client";
import { ErrorState } from "@/components/error-state";

import { RecipeForm, emptyToUndef, type RecipeFormValues } from "./form";

export function RecipeNewPage() {
  const navigate = useNavigate();
  const { from } = useSearch({ from: "/chemistry-profile/recipes/new" });
  const create = useCreateRecipe();
  // When cloning (`?from=<id>`) pre-fill from the source recipe's latest
  // version so a read-only system recipe becomes an editable tenant copy.
  const source = useRecipe(from);

  function submit(values: RecipeFormValues) {
    create.mutate(
      {
        id: newULID(),
        name: values.name,
        description: emptyToUndef(values.description),
        entries: values.entries.map((e) => ({
          order_rank: Number(e.order_rank),
          nutrient_id: e.nutrient_id,
          ml_per_gal: Number(e.ml_per_gal),
          notes: emptyToUndef(e.notes),
        })),
      },
      {
        onSuccess: () => navigate({ to: "/chemistry-profile/recipes" }),
      },
    );
  }

  if (from && source.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (from && source.error)
    return <ErrorState error={source.error} retry={source.refetch} />;

  const initial =
    from && source.data
      ? { ...source.data, name: `${source.data.name} (copy)` }
      : undefined;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">
        {initial ? "Clone recipe" : "New recipe"}
      </h1>
      <RecipeForm
        initial={initial}
        submitting={create.isPending}
        onSubmit={submit}
        submitLabel={initial ? "Create copy" : "Create"}
        banner={
          create.error ? (
            <ErrorState error={create.error} retry={() => create.reset()} />
          ) : undefined
        }
      />
    </div>
  );
}
